import React, { useState, createContext, useContext } from 'react';
import { VendorProduct, VendorOrder, VendorStats, VendorStore } from '../types';
// Mock Vendor Data
const MOCK_VENDOR_STORE: VendorStore = {
  id: 'v1',
  name: 'Kingsley Electronics',
  rating: 4.5,
  totalReviews: 234,
  description: 'Premium electronics and gadgets',
  businessInfo: {
    businessName: 'Kingsley Electronics Ltd',
    businessAddress: '123 Market Street, Lagos, Nigeria',
    taxId: 'TIN-123456789'
  },
  bankDetails: {
    accountName: 'Kingsley Electronics Ltd',
    accountNumber: '0123456789',
    bankName: 'First Bank of Nigeria'
  }
};
const MOCK_VENDOR_PRODUCTS: VendorProduct[] = [{
  id: 'vp1',
  name: 'iPhone 14 Pro Max',
  category: 'Smartphones',
  price: 850000,
  discountPrice: 800000,
  description: 'Latest iPhone with advanced camera system',
  images: ['https://placehold.co/400x400/e5e7eb/9ca3af?text=iPhone+14'],
  stock: 15,
  isActive: true,
  createdAt: '2024-01-15'
}, {
  id: 'vp2',
  name: 'Samsung Galaxy S23 Ultra',
  category: 'Smartphones',
  price: 750000,
  description: 'Flagship Samsung phone with S Pen',
  images: ['https://placehold.co/400x400/e5e7eb/9ca3af?text=Galaxy+S23'],
  stock: 8,
  isActive: true,
  createdAt: '2024-01-20'
}, {
  id: 'vp3',
  name: 'MacBook Pro 16"',
  category: 'Laptops',
  price: 1500000,
  description: 'Powerful laptop for professionals',
  images: ['https://placehold.co/400x400/e5e7eb/9ca3af?text=MacBook+Pro'],
  stock: 0,
  isActive: false,
  createdAt: '2024-02-01'
}];
const MOCK_VENDOR_ORDERS: VendorOrder[] = [{
  id: 'VO-001',
  buyerName: 'John Doe',
  buyerPhone: '+234 800 123 4567',
  product: {
    id: 'vp1',
    name: 'iPhone 14 Pro Max',
    image: 'https://placehold.co/100x100/e5e7eb/9ca3af?text=iPhone',
    price: 800000,
    quantity: 1
  },
  total: 800000,
  paymentStatus: 'Paid',
  orderStatus: 'New',
  shippingAddress: '45 Allen Avenue, Ikeja, Lagos',
  date: new Date().toISOString()
}, {
  id: 'VO-002',
  buyerName: 'Jane Smith',
  buyerPhone: '+234 800 987 6543',
  product: {
    id: 'vp2',
    name: 'Samsung Galaxy S23 Ultra',
    image: 'https://placehold.co/100x100/e5e7eb/9ca3af?text=Galaxy',
    price: 750000,
    quantity: 1
  },
  total: 750000,
  paymentStatus: 'COD',
  orderStatus: 'Processing',
  shippingAddress: '12 Victoria Island, Lagos',
  date: new Date(Date.now() - 86400000).toISOString()
}, {
  id: 'VO-003',
  buyerName: 'Mike Johnson',
  buyerPhone: '+234 800 555 1234',
  product: {
    id: 'vp1',
    name: 'iPhone 14 Pro Max',
    image: 'https://placehold.co/100x100/e5e7eb/9ca3af?text=iPhone',
    price: 800000,
    quantity: 2
  },
  total: 1600000,
  paymentStatus: 'Paid',
  orderStatus: 'Shipped',
  shippingAddress: '78 Lekki Phase 1, Lagos',
  date: new Date(Date.now() - 172800000).toISOString()
}];
interface VendorContextType {
  store: VendorStore;
  products: VendorProduct[];
  orders: VendorOrder[];
  stats: VendorStats;
  addProduct: (product: Omit<VendorProduct, 'id' | 'createdAt'>) => void;
  updateProduct: (id: string, product: Partial<VendorProduct>) => void;
  deleteProduct: (id: string) => void;
  updateOrderStatus: (orderId: string, status: VendorOrder['orderStatus']) => void;
  balance: number;
  pendingPayout: number;
  totalEarnings: number;
}
const VendorContext = createContext<VendorContextType | undefined>(undefined);
export function VendorProvider({
  children
}: {
  children: ReactNode;
}) {
  const [store] = useState<VendorStore>(MOCK_VENDOR_STORE);
  const [products, setProducts] = useState<VendorProduct[]>(MOCK_VENDOR_PRODUCTS);
  const [orders, setOrders] = useState<VendorOrder[]>(MOCK_VENDOR_ORDERS);
  // Calculate stats
  const stats: VendorStats = {
    ordersToday: orders.filter(o => new Date(o.date).toDateString() === new Date().toDateString()).length,
    revenueToday: orders.filter(o => new Date(o.date).toDateString() === new Date().toDateString() && o.paymentStatus === 'Paid').reduce((sum, o) => sum + o.total, 0),
    pendingOrders: orders.filter(o => o.orderStatus === 'New').length,
    rating: store.rating
  };
  const addProduct = (product: Omit<VendorProduct, 'id' | 'createdAt'>) => {
    const newProduct: VendorProduct = {
      ...product,
      id: `vp${Date.now()}`,
      createdAt: new Date().toISOString()
    };
    setProducts(prev => [newProduct, ...prev]);
  };
  const updateProduct = (id: string, updates: Partial<VendorProduct>) => {
    setProducts(prev => prev.map(p => p.id === id ? {
      ...p,
      ...updates
    } : p));
  };
  const deleteProduct = (id: string) => {
    setProducts(prev => prev.filter(p => p.id !== id));
  };
  const updateOrderStatus = (orderId: string, status: VendorOrder['orderStatus']) => {
    setOrders(prev => prev.map(o => o.id === orderId ? {
      ...o,
      orderStatus: status
    } : o));
  };
  // Wallet calculations
  const totalEarnings = orders.filter(o => o.paymentStatus === 'Paid').reduce((sum, o) => sum + o.total, 0);
  const pendingPayout = orders.filter(o => o.paymentStatus === 'Paid' && o.orderStatus === 'Delivered').reduce((sum, o) => sum + o.total, 0) * 0.85; // 15% commission
  const balance = pendingPayout * 0.5; // Assume 50% is available for withdrawal
  return <VendorContext.Provider value={{
    store,
    products,
    orders,
    stats,
    addProduct,
    updateProduct,
    deleteProduct,
    updateOrderStatus,
    balance,
    pendingPayout,
    totalEarnings
  }} data-id="element-166">
      {children}
    </VendorContext.Provider>;
}
export function useVendor() {
  const context = useContext(VendorContext);
  if (!context) {
    throw new Error('useVendor must be used within VendorProvider');
  }
  return context;
}